import { equals, groupBy } from '@bugbug/core/utils/toolbox';
import { normalize } from 'normalizr';

import type {
  NormalizedTestState,
  StartMultipleTestsBody,
  StartRecorderBody,
  StartRunnerAndFinishBody,
  StartRunnerAndPauseBody,
  StartRunnerAndRecordBody,
  StopRecorderBody,
} from './tests.types';
import type { TemporaryStep } from '../steps/steps.types';
import type { PatchCollection } from '@reduxjs/toolkit/dist/query/core/buildThunks';

import type { Component } from '@bugbug/core/types/components';
import type { Group, Grouping } from '@bugbug/core/types/groups';
import type { Project } from '@bugbug/core/types/projects';
import type { Step } from '@bugbug/core/types/steps';
import type { CurrentTestGrouping, Test, TestRun } from '@bugbug/core/types/tests';
import type { Maybe, SideEffect } from '@bugbug/core/types/utils';
import { ACTIONS, ENTITIES, showInternalServerError } from '~/services/toasts/internalServerError';

import { baseApi } from '../baseApi/baseApi';
import { COMPONENTS_API_TAGS } from '../components/components.constants';

import { TESTS_API_TAGS } from './tests.constants';
import { groupSchema } from './tests.schema';
import { getElementsScreenshotsFromSteps, normalizeTest } from './tests.utils';

export const createGroupTag = ({ originId }: { originId: Group['id'] }) => ({
  type: TESTS_API_TAGS.TEST_TAG,
  id: originId,
});

export const componentsRefreshTag = () => [
  COMPONENTS_API_TAGS.COMPONENT_TAG,
  COMPONENTS_API_TAGS.COMPONENTS_LIST_TAG,
];

export const testsApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    // GET /tests/
    getTests: builder.query<
      Test[],
      { query?: string; ordering?: string; projectId: Project['id'] }
    >({
      query: ({ query, ordering = 'name', projectId }) => ({
        url: '/tests/',
        method: 'GET',
        params: {
          query,
          ordering,
          project_id: projectId,
          pagination: 'off',
        },
      }),
      serializeQueryArgs: ({ queryArgs: { projectId } }) => `${projectId}-tests`,
      forceRefetch({ currentArg, previousArg }) {
        return !equals(currentArg, previousArg);
      },
      providesTags: (result) => {
        if (!result) {
          return [TESTS_API_TAGS.TESTS_LIST_TAG];
        }
        return [
          ...result.map((test) => ({ type: TESTS_API_TAGS.TEST_TAG, id: test.id })),
          TESTS_API_TAGS.TESTS_LIST_TAG,
        ];
      },
    }),

    // DELETE /tests/
    deleteTests: builder.mutation<void, { ids?: Test['id'][]; projectId?: Project['id'] }>({
      query: ({ ids, projectId }) => ({
        url: '/tests/',
        method: 'DELETE',
        data: {
          testsIds: ids,
          projectId,
        },
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        const patches = updateTestsListQuery(getState, dispatch, (draft) => {
          // Remove only selected tests
          if (arg.ids) {
            return draft.filter((test) => !arg.ids?.includes(test.id));
          }

          // Remove all tests
          return [];
        });

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
    }),

    // GET /tests/<testId>/
    getTest: builder.query<Test, { id: Test['id'] }>({
      query: ({ id }) => ({
        url: `/tests/${id}/`,
        method: 'GET',
      }),
      providesTags: (result) => {
        if (!result) {
          return [{ type: TESTS_API_TAGS.TEST_TAG, id: 'ELEMENT' }];
        }
        return [{ type: TESTS_API_TAGS.TEST_TAG, id: result.id }];
      },
    }),

    // GET /tests/<testId>/
    getCurrentTest: builder.query<
      NormalizedTestState,
      { id: Test['id']; testRunId?: TestRun['id'] }
    >({
      query: ({ id, testRunId }) => ({
        url: `/tests/${id}/`,
        method: 'GET',
        params: {
          testRunId,
          expand: 'test_run',
        },
      }),
      keepUnusedDataFor: 0,
      transformResponse: normalizeTest,
      providesTags: (result) => {
        if (!result) {
          return [{ type: TESTS_API_TAGS.TEST_TAG, id: 'NORMALIZED_ELEMENT' }];
        }
        return [
          {
            type: TESTS_API_TAGS.TEST_TAG,
            id: result.test.id,
            testRunId: result.test.testRun?.id,
          },
        ];
      },
    }),

    // POST /tests/
    createTest: builder.mutation<
      Test,
      {
        data: Pick<Test, 'name' | 'screenSizeType' | 'runProfileId'> & {
          group?: { id: Grouping['id']; testId: Test['id'] };
        };
        projectId: Project['id'];
      }
    >({
      query: ({ data: { group = {}, ...data }, projectId }) => ({
        url: '/tests/',
        method: 'POST',
        data: {
          ...data,
          testId: group.testId,
          groupId: group.id,
          name: data.name || null,
          projectId,
        },
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data: createdTest } = await queryFulfilled;

          updateTestsListQuery(getState, dispatch, (draft) => {
            draft.unshift({
              ...createdTest,
              hasSteps: false,
            });
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
    }),

    // PATCH /tests/<testId>/
    updateTest: builder.mutation<
      Test,
      {
        id: Test['id'];
        data: Partial<Test>;
      }
    >({
      query: ({ id, data }) => ({
        url: `/tests/${id}/`,
        method: 'PATCH',
        data,
      }),
      onQueryStarted: async ({ id, data }, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled;

          // TODO: Migrate to optimistic update when EditableText will be ready fo this change
          // Currently setting an error in EditableText doesn't work properly if we update the state prematurely
          updateCurrentTestQuery(getState, dispatch, (draft) => {
            Object.assign(draft.test, data);
          });

          updateTestsListQuery(getState, dispatch, (draft) => {
            const currentTestIndex = draft.findIndex((test) => test.id === id);
            if (currentTestIndex !== -1) {
              draft.splice(currentTestIndex, 1, { ...draft[currentTestIndex], ...data });
            }
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
    }),

    // POST /tests/<testId>/clone/
    duplicateTest: builder.mutation<Test, { id: Test['id']; data?: Partial<Test> }>({
      query: ({ id, data }) => ({
        url: `/tests/${id}/clone/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [TESTS_API_TAGS.TESTS_LIST_TAG, ...componentsRefreshTag()],
    }),

    // POST /tests/<testId>/unlink-component/
    unlinkComponent: builder.mutation<Group, { testId: Test['id']; componentId: Component['id'] }>({
      query: ({ testId, componentId }) => ({
        url: `/tests/${testId}/unlink-component/`,
        method: 'POST',
        data: {
          groupId: componentId,
        },
      }),
      onQueryStarted: async ({ componentId }, { getState, dispatch, queryFulfilled }) => {
        try {
          const { data: newGroup } = await queryFulfilled;

          updateCurrentTestQuery(getState, dispatch, (draft) => {
            const currentComponentIndex = draft.test.groups.indexOf(componentId);
            removeGroupsFromCurrentTest(draft, { ids: [componentId] });
            addNewGroupToCurrentTest(draft, {
              newGroup,
              atIndex: currentComponentIndex,
            });
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
      invalidatesTags: componentsRefreshTag,
    }),

    // POST /tests/<testId>/paste-steps/
    pasteSteps: builder.mutation<
      { group: Group; newStepsIds: Step['id'][] },
      { testId: Test['id']; groupId: Group['id']; atIndex: number; stepsIds: Step['id'][] }
    >({
      query: ({ testId, stepsIds, groupId, atIndex }) => ({
        url: `/tests/${testId}/paste-steps/`,
        method: 'POST',
        data: {
          groupId,
          atIndex,
          stepsIds,
        },
      }),
      onQueryStarted: async ({ groupId }, { getState, dispatch, queryFulfilled }) => {
        try {
          const {
            data: { group },
          } = await queryFulfilled;

          updateCurrentTestQuery(getState, dispatch, (draft) => {
            // TODO: Could be replaced with separated `getGroup` query + upsert
            if (draft.groups[groupId]) {
              updateGroupInCurrentTest(draft, { group });
            } else {
              addNewGroupToCurrentTest(draft, {
                newGroup: group,
                atIndex: draft.test.groups.length,
              });
            }
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
      invalidatesTags: [TESTS_API_TAGS.TESTS_LIST_TAG],
    }),

    insertComponent: builder.mutation<
      Component,
      { testId: Test['id']; componentId: Component['id']; atIndex: number }
    >({
      query: ({ testId, componentId, atIndex }) => ({
        url: `/tests/${testId}/insert-group/`,
        method: 'POST',
        data: {
          groupId: componentId,
          atIndex,
        },
      }),
      onQueryStarted: async ({ atIndex }, { dispatch, getState, queryFulfilled }) => {
        const { data: component } = await queryFulfilled;

        updateCurrentTestQuery(getState, dispatch, (draft) => {
          addNewGroupToCurrentTest(draft, { newGroup: component, atIndex });
        });
      },
      invalidatesTags: [TESTS_API_TAGS.TESTS_LIST_TAG, ...componentsRefreshTag()],
    }),

    // POST /tests/<testId>/group-relations/<groupId>/
    deleteGroupOnlyFromTest: builder.mutation<
      { test: Test; stepsIds: Step['id'][] },
      { testId: Test['id']; groupId: Group['id'] }
    >({
      query: ({ testId, groupId }) => ({
        url: `/tests/${testId}/group-relations/${groupId}/`,
        method: 'DELETE',
      }),
      onQueryStarted: async ({ groupId }, { getState, dispatch, queryFulfilled }) => {
        const patchResults = updateCurrentTestQuery(getState, dispatch, (draft) => {
          removeGroupsFromCurrentTest(draft, { ids: [groupId] });
        });

        try {
          await queryFulfilled;
        } catch (error) {
          showInternalServerError(ENTITIES.GROUP, ACTIONS.DELETE, error as Error);
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
      invalidatesTags: [TESTS_API_TAGS.TESTS_LIST_TAG, ...componentsRefreshTag()],
    }),

    // POST /tests/<testId>/partial-delete/
    deletePartialsFromTest: builder.mutation<
      { steps: Step['id'][]; groups: Group['id'][] },
      { testId: Test['id']; stepsIds: Step['id'][]; groupsIds: Group['id'][] }
    >({
      query: ({ testId, stepsIds, groupsIds }) => ({
        url: `/tests/${testId}/partial-delete/`,
        method: 'POST',
        data: {
          steps: stepsIds,
          groups: groupsIds,
        },
      }),
      onQueryStarted: async ({ stepsIds, groupsIds }, { getState, dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;

          updateCurrentTestQuery(getState, dispatch, (draft) => {
            removeStepsFromCurrentTest(draft, { ids: stepsIds });
            removeGroupsFromCurrentTest(draft, { ids: groupsIds });
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
      invalidatesTags: [TESTS_API_TAGS.TESTS_LIST_TAG, ...componentsRefreshTag()],
    }),

    // POST /tests/<testId>/update-steps-position/
    updateStepsPosition: builder.mutation<
      { destinationGroupId: Group['id']; index: number; newStepsIds: Step['id'][] },
      {
        testId: Test['id'];
        stepId: Step['id'];
        fromGroupId: Group['id'];
        fromIndex: number;
        toGroupId: Group['id'];
        toIndex: number;
      }
    >({
      query: ({ testId, toGroupId, toIndex, stepId }) => ({
        url: `/tests/${testId}/update-steps-position/`,
        method: 'POST',
        data: {
          stepsIds: [stepId],
          destinationGroupId: toGroupId,
          index: toIndex,
        },
      }),
    }),

    // POST /tests/<testId>/update-group-position/
    updateGroupsPosition: builder.mutation<
      { group: Group; newStepsIds: Step['id'][] },
      {
        testId: Test['id'];
        groupsOrder: {
          groupId: Group['id'];
          index: number;
        }[];
      }
    >({
      query: ({ testId, groupsOrder }) => ({
        url: `/tests/${testId}/update-groups-position/`,
        method: 'POST',
        data: groupsOrder,
      }),
      onQueryStarted: async ({ groupsOrder }, { getState, dispatch, queryFulfilled }) => {
        const patchResults = updateCurrentTestQuery(getState, dispatch, (draft) => {
          draft.test.groups = groupsOrder
            .sort((a, b) => a.index - b.index)
            .map(({ groupId }) => groupId);
        });

        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),

    // POST /tests/<testId>/clear/
    clearTest: builder.mutation<void, { id: Test['id'] }>({
      query: ({ id }) => ({
        url: `/tests/${id}/clear/`,
        method: 'POST',
      }),
      onQueryStarted: async (_, { dispatch, getState, queryFulfilled }) => {
        try {
          await queryFulfilled;

          updateCurrentTestQuery(getState, dispatch, (draft) => {
            draft.test.groups = [];
            draft.groups = {};
            draft.steps = {};
            draft.elementsScreenshots = {};
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
      invalidatesTags: [TESTS_API_TAGS.TESTS_LIST_TAG, ...componentsRefreshTag()],
    }),

    // POST /extension/runner/start/
    runTest: builder.mutation<
      TestRun,
      StartRunnerAndFinishBody | StartRunnerAndRecordBody | StartRunnerAndPauseBody
    >({
      query: (body) => ({
        url: '/extension/runner/start/',
        method: 'POST',
        data: {
          ...body,
          websocketId: null,
        },
      }),
      onQueryStarted: async ({ testId }, { getState, dispatch, queryFulfilled }) => {
        try {
          const { data: testRun } = await queryFulfilled;

          updateCurrentTestQuery(getState, dispatch, (draft) => {
            if (draft.test?.id === testId) {
              draft.test.testRun = testRun;
            }
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
    }),

    // POST /tests/run-selected/
    runTests: builder.mutation<TestRun[], StartMultipleTestsBody>({
      query: (data) => ({
        url: '/tests/run-selected/',
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { getState, dispatch, queryFulfilled }) => {
        try {
          const { data: testRuns } = await queryFulfilled;

          const updatedTestsIds = groupBy('test.id', testRuns);
          updateTestsListQuery(getState, dispatch, (draft) =>
            draft.map((test) => {
              if (updatedTestsIds[test.id]) {
                const { test: __, ...result } = updatedTestsIds[test.id][0];
                return {
                  ...test,
                  lastUnfinishedUserRun: {
                    ...test.lastUnfinishedUserRun,
                    ...result,
                  },
                };
              }

              return test;
            }),
          );
        } catch {
          // Required to prevent unhandled promise error
        }
      },
    }),

    // POST /extension/runner/stop/
    stopTest: builder.mutation<TestRun, { testId: Test['id']; testRunId?: TestRun['id'] }>({
      query: ({ testId, testRunId }) => ({
        url: '/extension/runner/stop/',
        method: 'POST',
        data: {
          testId,
          testRunId,
        },
      }),
      onQueryStarted: async ({ testId }, { getState, dispatch, queryFulfilled }) => {
        try {
          const { data: testRun } = await queryFulfilled;

          updateCurrentTestQuery(getState, dispatch, (draft) => {
            if (draft.test?.id === testId) {
              draft.test.testRun = testRun;
            }
          });

          updateTestsListQuery(getState, dispatch, (draft) =>
            draft.map((test) => {
              if (test.id !== testId) return test;

              const testRunWithValidStatus = {
                ...test.testRun,
                // TODO: Remove if BE returns status properly
                status: 'stopped',
              };

              return {
                ...test,
                lastUnfinishedUserRun: testRunWithValidStatus,
                testRun: testRunWithValidStatus,
              };
            }),
          );
        } catch {
          // Required to prevent unhandled promise error
        }
      },
    }),

    // POST /extension/recorder/start/
    startRecording: builder.mutation<TestRun, StartRecorderBody>({
      query: (data) => ({
        url: '/extension/recorder/start/',
        method: 'POST',
        data: {
          ...data,
          websocketId: null,
        },
      }),
      onQueryStarted: async ({ testId }, { getState, dispatch, queryFulfilled }) => {
        try {
          const { data: testRun } = await queryFulfilled;

          updateCurrentTestQuery(getState, dispatch, (draft) => {
            if (draft.test?.id === testId) {
              draft.test.testRun = testRun;
            }
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
    }),

    // POST /extension/recorder/stop/
    stopRecording: builder.mutation<TestRun, StopRecorderBody>({
      query: (data) => ({
        url: '/extension/recorder/stop/',
        method: 'POST',
        data,
      }),
    }),

    // POST /groups/
    createGroup: builder.mutation<
      Group,
      { testId: Test['id']; name: Maybe<string>; atIndex: number; stepsIds: Step['id'][] }
    >({
      query: (data) => ({
        url: '/groups/',
        method: 'POST',
        data,
      }),
      onQueryStarted: async ({ atIndex }, { getState, dispatch, queryFulfilled }) => {
        try {
          const { data: createdGroup } = await queryFulfilled;

          updateCurrentTestQuery(getState, dispatch, (draft) => {
            addNewGroupToCurrentTest(draft, {
              newGroup: createdGroup,
              atIndex,
            });
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
    }),

    // GET /groups/<id>/
    getGroup: builder.query<Grouping, Group['id']>({
      query: (id) => ({
        url: `/groups/${id}/`,
        method: 'GET',
      }),
      providesTags: (result) => (result ? [createGroupTag(result)] : []),
    }),

    // POST /groups/<id>/rename/
    renameGroup: builder.mutation<Group, { id: Group['id']; originId: Group['id']; name: string }>({
      query: ({ id, name }) => ({
        url: `/groups/${id}/rename/`,
        method: 'POST',
        data: { name },
      }),
      onQueryStarted: async ({ id, name }, { dispatch, getState, queryFulfilled }) => {
        const patches = updateCurrentTestQuery(getState, dispatch, (draft) => {
          if (draft.groups?.[id]) {
            draft.groups[id].name = name;

            populateGroupUpdateToAllInstances(draft, { group: draft.groups[id] });
          }
        });

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
    }),

    // DELETE /groups/<id>/
    deleteGroup: builder.mutation<void, { id: Group['id'] }>({
      query: ({ id }) => ({
        url: `/groups/${id}/`,
        method: 'DELETE',
      }),
      onQueryStarted: async ({ id }, { dispatch, getState, queryFulfilled }) => {
        const patches = updateCurrentTestQuery(getState, dispatch, (draft) => {
          removeGroupsFromCurrentTest(draft, { ids: [id] });
        });

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: [TESTS_API_TAGS.TESTS_LIST_TAG],
    }),

    // POST /groups/<id>/clone/
    duplicateGroup: builder.mutation<Group, { id: Group['id']; testId: Test['id'] }>({
      query: ({ id, testId }) => ({
        url: `/groups/${id}/clone/`,
        method: 'POST',
        data: {
          testId,
        },
      }),
      onQueryStarted: async ({ id }, { getState, dispatch, queryFulfilled }) => {
        try {
          const { data: createdGroup } = await queryFulfilled;

          updateCurrentTestQuery(getState, dispatch, (draft) => {
            const currentGroupIndex = draft.test.groups.indexOf(id);
            addNewGroupToCurrentTest(draft, {
              newGroup: createdGroup,
              atIndex: currentGroupIndex + 1,
            });
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
    }),

    // POST /groups/<id>/split/
    splitGroup: builder.mutation<
      Record<Grouping['id'], Grouping[]>,
      {
        id: Group['id'];
        testId: Test['id'];
        atIndex: number;
        stepsIds: Step['id'][];
        stepAtIndex: number;
        name: Maybe<string>;
        testRunId: Maybe<TestRun['id']>;
      }
    >({
      query: ({ id, ...data }) => ({
        url: `/groups/${id}/split/`,
        method: 'POST',
        data,
      }),
      onQueryStarted: async (_, { getState, dispatch, queryFulfilled }) => {
        try {
          const { data: splitChangesMap } = await queryFulfilled;

          updateCurrentTestQuery(getState, dispatch, (draft) => {
            Object.entries(splitChangesMap).forEach(([sourceGroupId, groupsAfterSplit]) => {
              const sourceGroupIndex = draft.test.groups.indexOf(sourceGroupId);
              const [sourceGroup, createdGroup] = groupsAfterSplit;

              // Reinsert the source group
              removeGroupsFromCurrentTest(draft, { ids: [sourceGroupId] });
              addNewGroupToCurrentTest(draft, {
                newGroup: sourceGroup,
                atIndex: sourceGroupIndex,
              });

              // Insert the created group
              addNewGroupToCurrentTest(draft, {
                newGroup: createdGroup,
                atIndex: sourceGroupIndex + 1,
              });
            });
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
      invalidatesTags: componentsRefreshTag,
    }),

    // PATCH /groups/<id>/
    switchToComponent: builder.mutation<Group, { groupId: Group['id'] }>({
      query: ({ groupId }) => ({
        url: `/groups/${groupId}/`,
        method: 'PATCH',
        data: {
          isComponent: true,
        },
      }),
      onQueryStarted: async ({ groupId }, { dispatch, queryFulfilled, getState }) => {
        const patches = updateCurrentTestQuery(getState, dispatch, (draft) => {
          if (draft.groups?.[groupId]) {
            draft.groups[groupId].isComponent = true;
          }
        });

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
      invalidatesTags: componentsRefreshTag,
    }),

    // POST /groups/<groupId>/steps/<stepId>/clone/
    duplicateStepInGroup: builder.mutation<Step[], { groupId: Group['id']; stepId: Step['id'] }>({
      query: ({ groupId, stepId }) => ({
        url: `/groups/${groupId}/steps/${stepId}/clone/`,
        method: 'POST',
      }),
      onQueryStarted: async ({ groupId, stepId }, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data: duplicatedSteps } = await queryFulfilled;

          updateCurrentTestQuery(getState, dispatch, (draft) => {
            const sourceStepIndex = draft.groups?.[groupId].steps.indexOf(stepId) ?? -1;

            duplicatedSteps.forEach((duplicatedStep) => {
              addStepsToCurrentTestGroup(draft, {
                steps: [duplicatedStep],
                groupId: duplicatedStep.groupId,
                atIndex: sourceStepIndex + 1,
              });
            });
          });
        } catch {
          // Required to prevent unhandled promise error
        }
      },
    }),
  }),
});

export const {
  // Test
  useGetTestQuery,
  useInsertComponentMutation,
  useClearTestMutation,
  // Groups & Components
  useDeleteGroupOnlyFromTestMutation,
  useRenameGroupMutation,
  useSwitchToComponentMutation,
} = testsApi;

// Helpers
export const invalidateTestsList = (ids?: Test['id'][]) => {
  if (!ids) return testsApi.util.invalidateTags([TESTS_API_TAGS.TESTS_LIST_TAG]);
  return testsApi.util.invalidateTags(ids.map((id) => ({ type: TESTS_API_TAGS.TEST_TAG, id })));
};

const patchAllQueriesByName = (
  queryName,
  tags,
  getState,
  dispatch,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callback: (...args: any) => void,
) => {
  const queries = testsApi.util.selectInvalidatedBy(getState(), tags);
  const patches: PatchCollection[] = [];

  queries
    .filter(({ endpointName }) => endpointName === queryName)
    .forEach((query) => {
      const patch = dispatch(
        testsApi.util.updateQueryData(queryName, query.originalArgs, callback),
      );

      patches.push(patch);
    });

  return patches;
};

export const updateTestsListQuery = (getState, dispatch, callback: SideEffect<Test[]>) =>
  patchAllQueriesByName('getTests', [TESTS_API_TAGS.TESTS_LIST_TAG], getState, dispatch, callback);

export const updateCurrentTestQuery = (
  getState,
  dispatch,
  callback: SideEffect<NormalizedTestState>,
) =>
  patchAllQueriesByName('getCurrentTest', [TESTS_API_TAGS.TEST_TAG], getState, dispatch, callback);

export const addStepsToCurrentTestGroup = (
  draft: NormalizedTestState,
  {
    steps,
    groupId,
    atIndex,
  }: { steps: (Step | TemporaryStep)[]; atIndex?: number; groupId: Group['id'] },
) => {
  steps.forEach((step) => {
    draft.steps[step.id] = step;
    draft.elementsScreenshots[step.id] = step.screenshot;
    draft.groups[groupId].steps = draft.groups[groupId].steps.toSpliced(
      atIndex ?? draft.groups[groupId].steps.length,
      0,
      step.id,
    );
  });
};

export const removeStepsFromCurrentTest = (
  draft: NormalizedTestState,
  { ids }: { ids: Step['id'][] },
) => {
  ids.forEach((stepId) => {
    const step = draft.steps[stepId];
    if (step) {
      const group = draft.groups[step.groupId];
      const stepIndex = group.steps.indexOf(stepId);
      if (stepIndex !== -1) {
        group.steps.splice(stepIndex, 1);
      }
      delete draft.steps[stepId];
    }
  });
};

export const removeGroupsFromCurrentTest = (
  draft: NormalizedTestState,
  { ids }: { ids: Step['id'][] },
) => {
  ids.forEach((groupId) => {
    const groupIndex = draft.test.groups.indexOf(groupId);
    if (groupIndex !== -1) {
      draft.test.groups.splice(groupIndex, 1);
    }
    if (draft.groups?.[groupId]) {
      delete draft.groups[groupId];
    }
  });
};

export const addNewGroupToCurrentTest = (
  draft: NormalizedTestState,
  { newGroup, atIndex }: { newGroup: Group | Component; atIndex: number },
) => {
  const newGroupPosition = Math.min(draft.test.groups.length, atIndex);
  draft.test.groups.splice(newGroupPosition, 0, newGroup.id);
  updateGroupInCurrentTest(draft, { group: newGroup });
};

export const updateGroupInCurrentTest = (
  draft: NormalizedTestState,
  { group }: { group: Group | Component },
) => {
  const { entities } = normalize(group, groupSchema);
  const { groups = {}, steps = {} } = entities;

  Object.assign(draft.groups, groups);
  Object.assign(draft.steps, steps);
  Object.assign(draft.elementsScreenshots, getElementsScreenshotsFromSteps(steps));

  const normalizedGroup = groups[group.id] as CurrentTestGrouping;
  populateGroupUpdateToAllInstances(draft, { group: normalizedGroup });
};

export const updateNormalizedGroupInCurrentTest = (
  draft: NormalizedTestState,
  { group }: { group: CurrentTestGrouping },
) => {
  if (!draft.groups[group.id]) {
    draft.test.groups = [...new Set([...draft.test.groups, group.id])];
  }

  Object.assign(draft.groups, { [group.id]: group });
  populateGroupUpdateToAllInstances(draft, { group });
};

export const populateGroupUpdateToAllInstances = (
  draft: NormalizedTestState,
  { group }: { group: Partial<CurrentTestGrouping> },
) => {
  const updatedInstances = Object.values(draft.groups).reduce(
    (updates, componentInstance) => {
      if (componentInstance.originId === group.originId) {
        updates[componentInstance.id] = {
          ...componentInstance,
          ...group,
          id: componentInstance.id,
        };
      }

      return updates;
    },
    {} as Record<Grouping['id'], CurrentTestGrouping>,
  );

  Object.assign(draft.groups, updatedInstances);
};
